import type { CSSProperties } from 'react';
import { Link } from 'react-router';
import { useStaff } from '../lib/staff.tsx';

export interface HistoryLinkProps {
  /** The audited table, e.g. 'ingredients', 'recipes', 'tasting_campaigns', 'customers'. */
  entityType: string;
  entityId: string | null | undefined;
  /** The record's name, read to screen readers as "History of <name>". */
  name?: string | undefined;
  /** Link text (default "History"). */
  label?: string | undefined;
  style?: CSSProperties | undefined;
  className?: string | undefined;
}

/** The audit trail URL filtered to one record. */
export function historyHref(entityType: string, entityId: string): string {
  const q = new URLSearchParams({ entity_type: entityType, entity_id: entityId });
  return `/audit?${q.toString()}`;
}

/** A small "History" link to the audit trail for one record. Hidden without audit.read. */
export function HistoryLink({
  entityType,
  entityId,
  name,
  label = 'History',
  style,
  className,
}: HistoryLinkProps) {
  const { can } = useStaff();
  if (!entityId || !can('audit.read')) return null;
  return (
    <Link
      to={historyHref(entityType, entityId)}
      className={className}
      aria-label={name ? `${label} of ${name}` : undefined}
      title="Every change to this record: who, when and what"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        minHeight: 'var(--touch)',
        fontSize: 14,
        fontWeight: 600,
        color: 'var(--ink-soft)',
        textDecoration: 'underline',
        textUnderlineOffset: 3,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      <svg
        aria-hidden="true"
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      >
        <circle cx="8" cy="8" r="6.2" />
        <path d="M8 4.6V8l2.3 1.5" />
      </svg>
      {label}
    </Link>
  );
}
